import { CSSProperties, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import CircularProgress from '@mui/material/CircularProgress';
import { APP_DOMAIN } from 'utils/urls';

type AvatarPickerModalProps = {
  open: boolean;
  currentAvatar: string;
  saving?: boolean;
  message?: string;
  messageType?: 'success' | 'error';
  onClose: () => void;
  onConfirm: (avatar: string) => void;
  onSelect?: () => void;
};

const AvatarPickerModal = ({
  open,
  currentAvatar,
  saving = false,
  message = '',
  messageType = 'success',
  onClose,
  onConfirm,
  onSelect
}: AvatarPickerModalProps) => {
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const [avatars, setAvatars] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const isSaveSuccess = messageType === 'success' && !!message;

  useEffect(() => {
    if (open) {
      setSelectedAvatar(currentAvatar);
    }
  }, [open, currentAvatar]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadAvatars = async () => {
      setLoading(true);

      try {
        const response = await fetch(`https://media.${APP_DOMAIN}/meta/avatar.json`, {
          signal: controller.signal
        });
        const json = await response.json().catch(() => null);
        const avatarList = Array.isArray(json?.avatars) ? json.avatars : [];

        if (isMounted) {
          setAvatars(avatarList);
        }
      } catch (error) {
        if (isMounted) {
          console.error('[AvatarPickerModal] Failed to load avatar list:', error);
          setAvatars([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAvatars();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div style={backdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleWrapStyle}>
            <span style={titleStyle}>Change Avatar</span>
            <span style={subtitleStyle}>Choose from the avatar pool, then confirm to update it.</span>
          </div>
          <button type='button' onClick={onClose} style={closeButtonStyle} aria-label='Close avatar picker'>
            <CloseIcon style={{ fontSize: '1rem' }} />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={previewCardStyle}>
            <div style={previewAvatarWrapStyle}>
              {selectedAvatar ? (
                <img
                  src={`https://media.${APP_DOMAIN}/images/${selectedAvatar}`}
                  alt='Selected avatar'
                  style={previewAvatarStyle}
                />
              ) : (
                <PersonIcon style={{ fontSize: '2.2rem', color: 'rgba(255,255,255,0.35)' }} />
              )}
            </div>
            <div style={previewTextWrapStyle}>
              <span style={previewTitleStyle}>Selected avatar</span>
              <span style={previewSubtitleStyle}>{selectedAvatar ? 'Ready to save' : 'No avatar selected'}</span>
            </div>
            <div style={previewBadgeStyle}>
              <EditIcon style={{ fontSize: '0.85rem' }} />
            </div>
          </div>

          <div style={gridStyle}>
            {loading && <span style={helperTextStyle}>Loading avatar list...</span>}
            {!loading && avatars.length === 0 && (
              <span style={helperTextStyle}>No avatars available right now.</span>
            )}
            {!loading && avatars.map((avatar, index) => {
              const isSelected = avatar === selectedAvatar;
              return (
                <button
                  key={avatar}
                  type='button'
                  onClick={() => {
                    setSelectedAvatar(avatar);
                    onSelect?.();
                  }}
                  style={{
                    ...avatarOptionStyle,
                    ...(isSelected ? avatarOptionSelectedStyle : {})
                  }}
                  aria-label={`Select avatar ${index + 1}`}
                  disabled={saving}
                >
                  <img
                    src={`https://media.${APP_DOMAIN}/images/${avatar}`}
                    alt=""
                    style={avatarThumbStyle}
                  />
                  {isSelected && (
                    <span style={selectedTickStyle}>
                      <CheckCircleIcon style={{ fontSize: '1rem' }} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {message && (
            <div
              style={{
                ...saveMessageStyle,
                ...(messageType === 'error' ? saveErrorStyle : saveSuccessStyle)
              }}
              role={messageType === 'error' ? 'alert' : 'status'}
            >
              {message}
            </div>
          )}

          <div style={actionsStyle}>
            {!isSaveSuccess && (
              <button type='button' onClick={onClose} style={secondaryBtnStyle} disabled={saving}>
                Cancel
              </button>
            )}
            <button
              type='button'
              onClick={() => {
                if (isSaveSuccess) {
                  onClose();
                  return;
                }

                onConfirm(selectedAvatar);
              }}
              style={
                saving || (!message && (!selectedAvatar || selectedAvatar === currentAvatar))
                  ? disabledPrimaryBtnStyle
                  : primaryBtnStyle
              }
              disabled={saving || (!message && (!selectedAvatar || selectedAvatar === currentAvatar))}
              aria-label={saving ? 'Saving avatar' : isSaveSuccess ? 'Close' : 'Confirm Avatar'}
            >
              {saving ? (
                <span style={savingLabelStyle}>
                  <CircularProgress size={16} thickness={5} style={{ color: '#ffffff' }} />
                  Saving...
                </span>
              ) : isSaveSuccess ? (
                'Close'
              ) : (
                'Confirm Avatar'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AvatarPickerModal;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  background: 'rgba(0, 0, 0, 0.72)',
  backdropFilter: 'blur(6px)'
};

const modalStyle: CSSProperties = {
  width: '100%',
  maxWidth: '560px',
  maxHeight: 'calc(100dvh - 2rem)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '18px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'linear-gradient(180deg, rgba(14, 20, 31, 0.98), rgba(9, 14, 24, 0.98))',
  boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
  overflow: 'hidden'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem 1.1rem',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  flexShrink: 0
};

const titleWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem'
};

const titleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff'
};

const subtitleStyle: CSSProperties = {
  fontSize: '0.74rem',
  color: 'rgba(255,255,255,0.55)'
};

const closeButtonStyle: CSSProperties = {
  width: '2.1rem',
  height: '2.1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(255,255,255,0.65)',
  background: 'rgba(255,255,255,0.05)'
};

const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  padding: '1.1rem',
  overflowY: 'auto',
  minHeight: 0,
  overscrollBehavior: 'contain'
};

const previewCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.9rem',
  padding: '0.95rem 1rem',
  borderRadius: '14px',
  border: '1px solid rgba(233, 140, 38, 0.2)',
  background: 'linear-gradient(180deg, rgba(233, 140, 38, 0.08), rgba(255, 255, 255, 0.02))'
};

const previewAvatarWrapStyle: CSSProperties = {
  width: '58px',
  height: '58px',
  borderRadius: '999px',
  overflow: 'hidden',
  border: '2px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.04)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const previewAvatarStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
};

const previewTextWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem',
  minWidth: 0,
  flex: 1
};

const previewTitleStyle: CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#E98C26',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const previewSubtitleStyle: CSSProperties = {
  fontSize: '0.82rem',
  color: '#ffffff',
  overflowWrap: 'anywhere'
};

const previewBadgeStyle: CSSProperties = {
  width: '2rem',
  height: '2rem',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#E98C26',
  background: 'rgba(12,18,28,0.96)',
  border: '1px solid rgba(233,140,38,0.25)'
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
  gap: '0.75rem',
  maxHeight: '320px',
  overflowY: 'auto',
  padding: '0.15rem 0.35rem 0.35rem 0.15rem'
};

const helperTextStyle: CSSProperties = {
  gridColumn: '1 / -1',
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.62)',
  padding: '0.4rem 0.1rem'
};

const avatarOptionStyle: CSSProperties = {
  position: 'relative',
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.03)',
  padding: '0.55rem',
  cursor: 'pointer'
};

const avatarOptionSelectedStyle: CSSProperties = {
  border: '1px solid rgba(233, 140, 38, 0.65)',
  boxShadow: 'inset 0 0 0 2px rgba(233, 140, 38, 0.28)'
};

const avatarThumbStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain'
};

const selectedTickStyle: CSSProperties = {
  position: 'absolute',
  top: '6px',
  right: '6px',
  color: '#6ddf8b',
  background: 'rgba(9,14,24,0.92)',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '0.6rem',
  flexShrink: 0
};

const saveMessageStyle: CSSProperties = {
  padding: '0.72rem 0.85rem',
  borderRadius: '10px',
  fontSize: '0.8rem',
  fontWeight: 700,
  lineHeight: 1.35
};

const saveSuccessStyle: CSSProperties = {
  color: '#7fe29b',
  background: 'rgba(76,175,80,0.12)',
  border: '1px solid rgba(76,175,80,0.28)'
};

const saveErrorStyle: CSSProperties = {
  color: '#ff8a8a',
  background: 'rgba(200,40,40,0.14)',
  border: '1px solid rgba(200,40,40,0.32)'
};

const savingLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.45rem'
};

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '46px',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.88rem',
  fontWeight: 700,
  color: '#ffffff',
  background: 'linear-gradient(135deg, #E98C26 0%, #cf7616 100%)',
  boxShadow: '0 4px 14px rgba(233, 140, 38, 0.25)'
};

const disabledPrimaryBtnStyle: CSSProperties = {
  ...primaryBtnStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
  boxShadow: 'none'
};

const secondaryBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '46px',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.88rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.82)',
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.05)'
};
